import { useEffect, useRef, useState } from "react";
import {
	Box,
	CircularProgress,
	Dialog,
	IconButton,
	Paper,
	Tooltip,
	Typography,
} from "@mui/material";
import { Close, FiberManualRecord, Fullscreen, Language } from "@mui/icons-material";
import socketService from "../services/socketService";

// Room the page keeps around the screen (top bar, run header): the whole screen
// fits in the window, however wide the panel is
const PAGE_CHROME = 180;

// The running test's browser screen, streamed by the executor while this is
// mounted (see executor/src/liveView.js). Frames arrive only when the page
// changes, so a still page shows its last frame.
export default function LiveBrowserView({ executionId, status }) {
	const [frame, setFrame] = useState(null);
	const [ended, setEnded] = useState(false);
	const [fullScreen, setFullScreen] = useState(false);
	const panelRef = useRef(null);

	useEffect(() => {
		setFrame(null);
		setEnded(false);
		socketService.startLiveView(executionId, (data) => {
			if (data?.ended) setEnded(true);
			else if (data?.image) setFrame(data);
		});
		return () => socketService.stopLiveView(executionId);
	}, [executionId]);

	// Turned on below the fold of a short window: bring it into sight
	useEffect(() => {
		panelRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
	}, []);

	const ratio = frame?.width && frame?.height ? frame.width / frame.height : 16 / 10;

	const waitingText =
		status === "QUEUED"
			? "Waiting for a free slot…"
			: status === "PENDING"
				? "Starting the browser…"
				: "Waiting for the browser…";

	return (
		<Paper
			ref={panelRef}
			variant="outlined"
			sx={{ overflow: "hidden", mb: 2, scrollMarginTop: 80 }}
		>
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					gap: 1,
					px: 1.5,
					py: 0.75,
					bgcolor: "action.hover",
					borderBottom: 1,
					borderColor: "divider",
				}}
			>
				<FiberManualRecord
					sx={{ fontSize: 12, color: ended ? "text.disabled" : "error.main" }}
				/>
				<Typography variant="caption" fontWeight={600}>
					{ended ? "Ended" : "Live"}
				</Typography>
				<Language sx={{ fontSize: 16, color: "text.secondary", ml: 1 }} />
				<Typography
					variant="caption"
					color="text.secondary"
					noWrap
					title={frame?.url || ""}
					sx={{ flex: 1, minWidth: 0 }}
				>
					{frame?.url || "—"}
				</Typography>
				<Tooltip title="Full screen">
					<span>
						<IconButton
							size="small"
							aria-label="Full screen"
							disabled={!frame}
							onClick={() => setFullScreen(true)}
							sx={{ p: 0.25 }}
						>
							<Fullscreen fontSize="small" />
						</IconButton>
					</span>
				</Tooltip>
			</Box>
			<Box
				sx={{
					position: "relative",
					bgcolor: "background.default",
					aspectRatio: `${ratio}`,
					// As wide as the panel, unless that would make it taller than the
					// window: then narrower, so no part of the screen is cut off
					width: `min(100%, max(320px, calc((100vh - ${PAGE_CHROME}px) * ${ratio})))`,
					mx: "auto",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
				}}
			>
				{frame ? (
					<Box
						component="img"
						src={`data:image/jpeg;base64,${frame.image}`}
						alt="Live view of the test's browser"
						sx={{
							width: "100%",
							height: "100%",
							objectFit: "contain",
							display: "block",
							opacity: ended ? 0.6 : 1,
						}}
					/>
				) : (
					<Box sx={{ textAlign: "center", color: "text.secondary" }}>
						{!ended && <CircularProgress size={24} sx={{ mb: 1 }} />}
						<Typography variant="body2">
							{ended ? "The run has finished" : waitingText}
						</Typography>
					</Box>
				)}
			</Box>
			<Dialog
				fullScreen
				open={fullScreen && Boolean(frame)}
				onClose={() => setFullScreen(false)}
				PaperProps={{ sx: { bgcolor: "background.default" } }}
			>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 1,
						px: 2,
						py: 1,
						borderBottom: 1,
						borderColor: "divider",
					}}
				>
					<FiberManualRecord
						sx={{ fontSize: 12, color: ended ? "text.disabled" : "error.main" }}
					/>
					<Typography variant="body2" fontWeight={600}>
						{ended ? "Ended" : "Live"}
					</Typography>
					<Typography
						variant="body2"
						color="text.secondary"
						noWrap
						sx={{ flex: 1, minWidth: 0, ml: 1 }}
					>
						{frame?.url}
					</Typography>
					<Tooltip title="Exit full screen (Esc)">
						<IconButton aria-label="Exit full screen" onClick={() => setFullScreen(false)}>
							<Close />
						</IconButton>
					</Tooltip>
				</Box>
				{frame && (
					<Box
						component="img"
						src={`data:image/jpeg;base64,${frame.image}`}
						alt="Live view of the test's browser"
						sx={{
							flex: 1,
							minHeight: 0,
							width: "100%",
							objectFit: "contain",
							display: "block",
							p: 2,
							opacity: ended ? 0.6 : 1,
						}}
					/>
				)}
			</Dialog>
		</Paper>
	);
}
